import { AxiosResponse } from 'axios';
import { Client } from 'react-stomp-hooks';

import { axiosClient } from '../config/axios-client';
import { GameType } from '../model/enumeration/GameType.enum';
import { PokerRoom } from '../model/enumeration/PokerRoom';
import { WBotManualActionRequest } from '../model/websocket/WBotManualActionRequest';
import { WBotRebuyRequest } from '../model/websocket/WBotRebuyRequest';
import { WTableRemoveBotRequest } from '../model/websocket/WTableRemoveBotRequest';
import { WTableSitOutBotRequest } from '../model/websocket/WTableSitOutBotRequest';
import playerStore from './PlayerStore';
import tableStore from './TableStore';
import websocketStore from './WebsocketStore';

class BotManagementStore {
  public manualAction = (
    stompClient: Client,
    tableId: string,
    room: PokerRoom,
    botId: string,
    request: WBotManualActionRequest,
  ): void =>
    websocketStore.sendEvent(
      stompClient,
      `${this.getBotManagementBasePath(tableId, room, botId)}/manual-action`,
      request,
    );

  public removeBotFromTable = (
    stompClient: Client,
    tableId: string,
    room: PokerRoom,
    botId: string,
    request: WTableRemoveBotRequest,
  ): void => {
    websocketStore.sendEvent(
      stompClient,
      `${this.getBotManagementBasePath(tableId, room, botId)}/remove`,
      request,
    );
    if (request.onUTG) {
      this.modifyBotLeaveOnUTG(tableId, botId, true);
    } else {
      this.modifyBotLeaveTable(tableId, botId, true);
    }
  };

  public removeBotFromWaitingHost = (
    stompClient: Client,
    tableId: string,
    room: PokerRoom,
    botId: string,
  ): void =>
    websocketStore.sendEvent(
      stompClient,
      `${this.getBotManagementBasePath(
        tableId,
        room,
        botId,
      )}/remove-waiting-host`,
    );

  public cancelRemoveBotFromTableOnUTG = (
    stompClient: Client,
    tableId: string,
    room: PokerRoom,
    botId: string,
  ): void => {
    websocketStore.sendEvent(
      stompClient,
      `${this.getBotManagementBasePath(
        tableId,
        room,
        botId,
      )}/cancel-remove-on-utg`,
    );
    this.modifyBotLeaveOnUTG(tableId, botId, false);
  };

  public sitOutBotFromTable = (
    stompClient: Client,
    tableId: string,
    room: PokerRoom,
    botId: string,
    request: WTableSitOutBotRequest,
  ): void =>
    websocketStore.sendEvent(
      stompClient,
      `${this.getBotManagementBasePath(tableId, room, botId)}/sit-out`,
      request,
    );

  public changeTable = (
    stompClient: Client,
    tableId: string,
    room: PokerRoom,
    botId: string,
  ): void =>
    websocketStore.sendEvent(
      stompClient,
      `${this.getBotManagementBasePath(tableId, room, botId)}/change-table`,
    );

  public rebuy = (
    stompClient: Client,
    tableId: string,
    room: PokerRoom,
    botId: string,
    request: WBotRebuyRequest,
  ): void =>
    websocketStore.sendEvent(
      stompClient,
      `${this.getBotManagementBasePath(tableId, room, botId)}/rebuy`,
      request,
    );

  public forceJoinTable = (
    tableId: string,
    gameType: GameType,
    room: PokerRoom,
    botId: string,
    place: number,
    buyIn: number,
  ): Promise<AxiosResponse<void>> =>
    axiosClient.put(
      `${this.getBotManagementBasePath(tableId, room, botId)}/force-join-table`,
      {
        place,
        gameType,
        buyIn,
      },
    );

  public batchForceJoinTable = (
    tableId: string,
    room: PokerRoom,
    gameType: GameType,
    bots: { pid: string }[],
  ): Promise<AxiosResponse<void>> =>
    axiosClient.put(
      `${this.getBotBatchManagementBasePath(
        tableId,
        room,
      )}/batch-force-join-table`,
      { gameType, bots },
    );

  public scheduleBotSitting = (
    tableId: string,
    gameType: GameType,
    room: PokerRoom,
    botId: string,
    place: number,
    sitTime: string,
    buyIn,
  ): Promise<AxiosResponse<void>> =>
    axiosClient.post(
      `${this.getBotManagementBasePath(
        tableId,
        room,
        botId,
      )}/schedule-bot-sitting`,
      {
        gameType,
        place,
        sitTime,
        buyIn,
      },
    );

  public batchScheduleBotsSitting = (
    tableId: string,
    room: PokerRoom,
    gameType: GameType,
    bots: {
      pid: string;
      sitTime: string;
    }[],
  ): Promise<AxiosResponse<void>> =>
    axiosClient.post(
      `${this.getBotBatchManagementBasePath(
        tableId,
        room,
      )}/batch-schedule-bots-sitting`,
      { gameType, bots },
    );

  public cancelScheduledBotSitting = (
    tableId: string,
    room: PokerRoom,
    botId: string,
  ): Promise<AxiosResponse<void>> =>
    axiosClient.delete(
      `${this.getBotManagementBasePath(
        tableId,
        room,
        botId,
      )}/schedule-bot-sitting/cancel`,
    );

  private modifyBotLeaveOnUTG = (
    tableId: string,
    botId: string,
    leaveOnUTG: boolean,
  ): void => {
    tableStore.findActiveTableById(tableId).then(table => {
      table.setPlayers(
        playerStore.modifyPlayers(
          table.players,
          botId,
          player => (player.leaveOnUTG = leaveOnUTG),
        ),
      );
    });
  };

  private modifyBotLeaveTable = (
    tableId: string,
    botId: string,
    leaveTable: boolean,
  ): void => {
    tableStore.findActiveTableById(tableId).then(table => {
      table.setPlayers(
        playerStore.modifyPlayers(
          table.players,
          botId,
          player => (player.leaveTable = leaveTable),
        ),
      );
    });
  };

  private getBotManagementBasePath = (
    tableId: string,
    room: PokerRoom,
    botId: string,
  ): string => `/tables/${tableId}/${room}/bots/${botId}`;

  private getBotBatchManagementBasePath = (
    tableId: string,
    room: PokerRoom,
  ): string => `/tables/${tableId}/${room}/bots`;
}

const botManagementStore = new BotManagementStore();
export default botManagementStore;
